/**
 * Types and constants for the parts of the activation surface that live in the
 * browser rather than the database: which sections are nested inside
 * "Customise your pack", the per-pack reveal flag, and the placement checklist.
 */

// =====================================================================
// Page layout
// =====================================================================

/**
 * Anchor ids that are rendered INSIDE the collapsed "Customise your pack"
 * block on `ActivationPackDetailPage`. A deep link to any of these must open
 * that block before scrolling, because collapsed content is not in the DOM.
 *
 * `print-order` is deliberately absent — it stays above the fold.
 */
export const CUSTOMISE_SECTION_IDS: ReadonlySet<string> = new Set([
  'activation-assets',
  'activate-app',
  'activate-presentation',
  'activate-signature',
  'activate-lookbook',
  'activate-posters',
  'activate-invite',
  'activate-promote',
  'pack-settings',
]);

// =====================================================================
// Pack build narration
// =====================================================================

/** One named stage of the pack build, derived from what the pack actually has. */
export interface PackBuildStage {
  id: string;
  /** Present-tense narration shown while this stage is the active one. */
  label: string;
  /** Mascot pose shown while this stage is active. */
  mascot: string;
  /** True once every artifact this stage covers exists on the pack. */
  done: boolean;
}

// =====================================================================
// Placement checklist (client-side only — no backend)
// =====================================================================

export type PlacementSpotId =
  | 'front_door'
  | 'vehicle'
  | 'counter'
  | 'invoice'
  | 'email_footer';

export interface PlacementSpot {
  id: PlacementSpotId;
  label: string;
  hint: string;
}

/**
 * The lookbook placements we ask people to tick off. Deliberately short —
 * the point is a two-tap "yes, that one's up", not an inventory system.
 */
export const PLACEMENT_SPOTS: PlacementSpot[] = [
  { id: 'front_door', label: 'Front door', hint: 'Window or entrance sticker' },
  { id: 'vehicle', label: 'Van or vehicle', hint: 'Weatherproof QR sticker' },
  { id: 'counter', label: 'Counter', hint: 'Desk card or small poster' },
  { id: 'invoice', label: 'Invoices', hint: 'QR on quotes and invoices' },
  { id: 'email_footer', label: 'Email footer', hint: 'Signature widget in place' },
];

/** Which placements the customer has ticked, per activation pack. */
export type PlacementChecklist = Partial<Record<PlacementSpotId, boolean>>;

// =====================================================================
// Activation progress (PRINTED / PLACED / SCANNED)
// =====================================================================

export type ActivationStepState = 'todo' | 'in_progress' | 'done';

export interface ActivationProgress {
  /** From the real print order status. */
  printed: ActivationStepState;
  printedAt: string | null;
  printedVendorEmail: string | null;
  /** Client-side checklist only. */
  placed: ActivationStepState;
  placedCount: number;
  placedTotal: number;
  /** From real QR scan activity. */
  scanned: ActivationStepState;
  scansThisWeek: number;
  scansTotal: number;
  isLoading: boolean;
  /**
   * The honest nudge: the customer sent artwork to a printer but nothing has
   * ever been scanned. Consumed by the dashboard next-action line.
   */
  printedButNeverScanned: boolean;
}
